// components/portal/InventoryFilterControls.tsx
'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Filter } from '@/components/ui/lucide-memo';
import { AppTooltip } from '@/components/ui/tooltip';

export interface InventoryFilterOption {
  key: string;
  label: string;
  icon?: React.ReactNode;
  /** Predefined values → a Notion-style value picker instead of a free-text box. */
  values?: Array<{ value: string; label: string }>;
}

interface InventoryFilterControlsProps {
  filterOptions: InventoryFilterOption[];
  viewFilters: Record<string, unknown>;
  isFilterKeyLocked: (key: string) => boolean;
  mergeWithLockedFilters: (candidate: Record<string, unknown>) => Record<string, unknown>;
  setFilters: (next: Record<string, unknown>) => void;
}

export const InventoryFilterControls = ({
  filterOptions,
  viewFilters,
  isFilterKeyLocked,
  mergeWithLockedFilters,
  setFilters,
}: InventoryFilterControlsProps) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterFieldKey, setFilterFieldKey] = useState<string | null>(null);
  const [filterDraftValue, setFilterDraftValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // The popover had no way out: Escape did nothing, clicking away did nothing, and opening the
  // sort menu left both cards stacked on top of each other. Same handling the sibling sort
  // dropdown already uses, which also makes the two mutually exclusive for free, a click on the
  // sort button now counts as "outside" and closes this one.
  useEffect(() => {
    if (!isFilterOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsFilterOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsFilterOpen(false); };
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isFilterOpen]);

  const selectedFilterOption = useMemo(
    () => filterOptions.find((option) => option.key === filterFieldKey) || null,
    [filterFieldKey, filterOptions],
  );

  const availableFilterOptions = useMemo(
    () => filterOptions.filter((option) => !isFilterKeyLocked(option.key)),
    [filterOptions, isFilterKeyLocked],
  );

  const activeFilterChips = useMemo(
    () =>
      filterOptions
        .map((option) => {
          if (isFilterKeyLocked(option.key)) return null;
          const filterValue = viewFilters[option.key];
          const value =
            typeof filterValue === 'string'
              ? filterValue.trim()
              : typeof filterValue === 'number' && Number.isFinite(filterValue)
                ? String(filterValue)
                : '';
          if (!value) return null;
          // For predefined-value options, show the value's LABEL (e.g. "In France"), not the raw
          // stored value ("true").
          const display = option.values?.find((v) => v.value === value)?.label ?? value;
          return { key: option.key, label: option.label, value: display };
        })
        .filter(Boolean) as Array<{ key: string; label: string; value: string }>,
    [filterOptions, isFilterKeyLocked, viewFilters],
  );

  const applyFilterValue = useCallback((rawValue: string | undefined) => {
    if (!filterFieldKey || isFilterKeyLocked(filterFieldKey)) return;
    setFilters(
      mergeWithLockedFilters({
        ...viewFilters,
        [filterFieldKey]: (rawValue ?? '').trim() || undefined,
      }),
    );
    setIsFilterOpen(false);
    setFilterFieldKey(null);
  }, [filterFieldKey, isFilterKeyLocked, mergeWithLockedFilters, setFilters, viewFilters]);
  const handleApplyFilterValue = useCallback(() => applyFilterValue(filterDraftValue), [applyFilterValue, filterDraftValue]);
  const handleClearFilterValue = useCallback(() => applyFilterValue(undefined), [applyFilterValue]);

  const handleChipClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { filterKey, filterValue } = event.currentTarget.dataset;
    if (!filterKey) return;
    setFilterFieldKey(filterKey);
    setFilterDraftValue(filterValue ?? '');
    setIsFilterOpen(true);
  }, []);

  const handleToggleFilterPanel = useCallback(() => {
    setIsFilterOpen(!isFilterOpen);
    if (!isFilterOpen) setFilterFieldKey(null);
  }, [isFilterOpen]);

  const handleSelectFilterField = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const optionKey = event.currentTarget.dataset.optionKey;
    if (!optionKey) return;
    setFilterFieldKey(optionKey);
    setFilterDraftValue(String(viewFilters[optionKey] ?? ''));
  }, [viewFilters]);

  const handleClearFilterField = useCallback(() => setFilterFieldKey(null), []);

  const handlePredefinedValueClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { optionValue, active } = event.currentTarget.dataset;
    applyFilterValue(active === 'true' ? undefined : optionValue);
  }, [applyFilterValue]);

  const handleDraftValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setFilterDraftValue(event.target.value),
    [],
  );

  const handleDraftKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleApplyFilterValue();
  }, [handleApplyFilterValue]);

  const handleCancelFilterEdit = useCallback(() => {
    setIsFilterOpen(false);
    setFilterFieldKey(null);
  }, []);

  if (filterOptions.length === 0) return null;

  return (
    <>
      {activeFilterChips.map((chip) => (
        <button
          key={chip.key}
          data-filter-key={chip.key}
          data-filter-value={chip.value}
          onClick={handleChipClick}
          className="ml-1 inline-flex h-9 items-center gap-1 rounded-[999px] border border-[#0A84FF55] bg-[#0A84FF1A] px-3 text-[#0A84FF] transition-colors hover:bg-[#0A84FF24]"
        >
          <span className="text-[14px] leading-[14px] font-medium">
            {chip.label}: {chip.value}
          </span>
          <ChevronDown size={14} className="text-[#0A84FF]" />
        </button>
      ))}

      <div className="relative" ref={containerRef}>
        <AppTooltip content="Filter">
        <button
          onClick={handleToggleFilterPanel}
          aria-label="Filter"
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#E5E5E5] transition-colors hover:bg-white/[0.08] hover:text-white"
        >
          <Filter size={17} strokeWidth={2} />
        </button>
        </AppTooltip>

        {isFilterOpen && (
          <div className="absolute left-0 top-full z-50 mt-2 w-[370px] overflow-hidden rounded-[20px] border border-white/[0.06] bg-[#353535] p-2 shadow-[0_18px_52px_rgba(0,0,0,0.5)]">
            {!selectedFilterOption ? (
              <div className="space-y-1">
                <div className="px-1 pb-1 text-[13px] text-white/45">Choose filter field</div>
                {availableFilterOptions.map((option) => (
                  <button
                    key={option.key}
                    data-option-key={option.key}
                    onClick={handleSelectFilterField}
                    className="flex h-9 w-full items-center gap-2 rounded-[10px] px-2 text-left text-white/90 transition-colors hover:bg-[#232323]"
                  >
                    <span className="text-white/70">{option.icon}</span>
                    <span className="text-[14px] leading-[14px]">{option.label}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div>
                <div className="mb-2 flex items-center gap-1.5">
                  <button className="inline-flex h-9 items-center gap-1 rounded-[999px] bg-[#242424] px-3 text-[15px] font-semibold text-white">
                    <span>{selectedFilterOption.label}</span>
                  </button>
                  <button
                    onClick={handleClearFilterField}
                    className="ml-auto inline-flex h-9 items-center justify-center rounded-[10px] px-2 text-[20px] leading-none text-white/55 transition-colors hover:bg-[#242424] hover:text-white/80"
                  >
                    ...
                  </button>
                </div>
                {selectedFilterOption.values ? (
                  // Notion-style value picker: pick a predefined value; applies immediately.
                  <div className="space-y-1">
                    {selectedFilterOption.values.map((v) => {
                      const active = String(viewFilters[selectedFilterOption.key] ?? '') === v.value;
                      return (
                        <button
                          key={v.value}
                          data-option-value={v.value}
                          data-active={active ? 'true' : 'false'}
                          onClick={handlePredefinedValueClick}
                          className="flex h-9 w-full items-center justify-between gap-2 rounded-[10px] px-2 text-left text-[14px] text-white/90 transition-colors hover:bg-[#232323]"
                        >
                          <span>{v.label}</span>
                          {active && <Check size={15} className="text-[#0A84FF]" />}
                        </button>
                      );
                    })}
                    {viewFilters[selectedFilterOption.key] ? (
                      <button
                        onClick={handleClearFilterValue}
                        className="flex h-9 w-full items-center rounded-[10px] px-2 text-left text-[13px] text-white/50 transition-colors hover:bg-[#232323] hover:text-white/80"
                      >
                        Clear filter
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Type a value..."
                      className="h-11 w-full rounded-[12px] border border-[#0A84FF] bg-[#1F1F1F] px-3 text-[14px] text-white outline-none"
                      value={filterDraftValue}
                      onChange={handleDraftValueChange}
                      onKeyDown={handleDraftKeyDown}
                      autoFocus
                    />
                    <div className="mt-2 flex items-center justify-end gap-2">
                      <button
                        onClick={handleCancelFilterEdit}
                        className="h-8 rounded-full px-3 text-[13px] text-white/65 transition-colors hover:text-white"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleApplyFilterValue}
                        className="h-8 rounded-full bg-[#262626] px-3 text-[13px] text-white transition-colors hover:bg-[#303030]"
                      >
                        Apply
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

